import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Table } from 'rsuite';
import { Reload } from '@rsuite/icons';
import { useAuth } from '../utils/useAuth';
import { getApiUrl, getAuthHeaders, getUserId } from '../utils/apiConfig';
import MetricLineChart from './MetricLineChart';

const { Column, HeaderCell, Cell } = Table;

// First GPU in a sample's gpu list (we chart GPU 0).
function gpu0(s) {
  return s && Array.isArray(s.gpu) && s.gpu.length > 0 ? s.gpu[0] : null;
}

// Parse a docker-stats percentage string like "99.99%" -> 99.99 (or null).
function parsePct(str) {
  if (!str) return null;
  const m = String(str).match(/([\d.]+)\s*%/);
  return m ? parseFloat(m[1]) : null;
}

// Parse the "used" side of a docker MemUsage string ("362.3MiB / 222.2GiB")
// and normalise to MiB (or null).
function parseMemMiB(str) {
  if (!str) return null;
  const used = String(str).split('/')[0].trim();
  const m = used.match(/([\d.]+)\s*([KMGT]?i?B)/i);
  if (!m) return null;
  const val = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  const factor = { b: 1 / (1024 * 1024), kib: 1 / 1024, mib: 1, gib: 1024, tib: 1024 * 1024 };
  return val * (factor[unit] ?? 1);
}

function numOrNull(v) {
  return v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v);
}

// Short HH:MM:SS label from an ISO timestamp (used for chart X axis).
function tsLabel(ts) {
  if (!ts) return '';
  const m = String(ts).match(/T(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : String(ts);
}

// "2026-06-04T05:05:01Z" -> "2026-06-04 05:05:01" (drops the T and Z so the
// table cell renders on one line instead of wrapping on the trailing Z).
function tsFull(ts) {
  if (!ts) return '';
  const m = String(ts).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : String(ts);
}

// One entry per chartable parameter. Order defines the tab order.
const CHART_PARAMS = [
  { key: 'util', label: 'GPU Util', unit: '%', color: '#52c41a', yMin: 0, yMax: 100, get: (s) => gpu0(s)?.gpu_util },
  { key: 'gpumem', label: 'GPU Mem', unit: 'MiB', color: '#3498ff', yMin: 0, get: (s) => gpu0(s)?.vram_used_mib },
  { key: 'temp', label: 'Temp', unit: '°C', color: '#fa8c16', get: (s) => gpu0(s)?.temp_c },
  { key: 'power', label: 'Power', unit: 'W', color: '#f5222d', yMin: 0, get: (s) => gpu0(s)?.power_w },
  { key: 'cpu', label: 'CPU', unit: '%', color: '#722ed1', yMin: 0, get: (s) => parsePct(s.container?.CPUPerc) },
  { key: 'cmem', label: 'Container Mem', unit: 'MiB', color: '#13c2c2', yMin: 0, get: (s) => parseMemMiB(s.container?.MemUsage) },
];

function parseMetricsJsonl(text) {
  const samples = [];
  if (!text) return samples;
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      samples.push(JSON.parse(trimmed));
    } catch {
      // skip malformed lines
    }
  }
  return samples;
}

function formatGpuUtil(gpuList) {
  if (!gpuList || gpuList.length === 0) return '—';
  return gpuList.map((g) => `${g.gpu_util}%`).join(' / ');
}

function formatGpuMem(gpuList) {
  if (!gpuList || gpuList.length === 0) return '—';
  return gpuList
    .map((g) => `${g.vram_used_mib} / ${g.vram_total_mib} MiB`)
    .join(' | ');
}

function formatGpuTemp(gpuList) {
  if (!gpuList || gpuList.length === 0) return '—';
  return gpuList.map((g) => `${g.temp_c}°C`).join(' / ');
}

function formatGpuPower(gpuList) {
  if (!gpuList || gpuList.length === 0) return '—';
  return gpuList.map((g) => `${g.power_w} W`).join(' / ');
}

function gpuUtilStyle(gpuList) {
  if (!gpuList || gpuList.length === 0) return {};
  const maxUtil = Math.max(...gpuList.map((g) => g.gpu_util));
  const color = maxUtil >= 80 ? '#52c41a' : maxUtil >= 20 ? '#faad14' : '#999';
  return { color, fontWeight: 'bold' };
}

function formatContainerCpu(container) {
  if (!container) return '—';
  return container.CPUPerc || container.cpu || '—';
}

function formatContainerMem(container) {
  if (!container) return '—';
  return container.MemUsage || container.mem_usage || '—';
}

function MetricsDialog({ show, onClose, jobId }) {
  const auth = useAuth();
  const [rawMetrics, setRawMetrics] = useState('');
  const [sampleCount, setSampleCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('table');

  const samples = useMemo(() => parseMetricsJsonl(rawMetrics), [rawMetrics]);
  const chartParam = useMemo(() => CHART_PARAMS.find((p) => p.key === view) || null, [view]);

  const fetchMetrics = useCallback(async () => {
    if (!jobId) return;

    setLoading(true);
    setError(null);

    const userId = getUserId(auth.user);
    if (!userId) {
      setError('User ID not found. Please set up your user ID first.');
      setLoading(false);
      return;
    }

    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(
        getApiUrl(`/users/${userId}/jobs/${jobId}/metrics`),
        { method: 'GET', headers }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch metrics');
      }

      const data = await response.json();
      setRawMetrics(data.metrics || '');
      setSampleCount(data.sample_count ?? parseMetricsJsonl(data.metrics || '').length);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setRawMetrics('');
      setSampleCount(0);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, auth.user, auth.getAccessTokenSilently]);

  useEffect(() => {
    if (show && jobId) {
      fetchMetrics();
    } else {
      setRawMetrics('');
      setSampleCount(0);
      setError(null);
      setView('table');
    }
  }, [show, jobId, fetchMetrics]);

  return (
    <Modal open={show} onClose={onClose} size="lg" fullscreen>
      <Modal.Header>
        <Modal.Title>Metrics for Job {jobId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div>Loading metrics...</div>
        ) : error ? (
          <div style={{ color: '#c00' }}>{error}</div>
        ) : samples.length === 0 ? (
          <div>No metrics collected yet. Metrics appear while the job is running (about every 60s).</div>
        ) : (
          <>
            <p style={{ marginBottom: 12 }}>
              {sampleCount} sample(s) — GPU utilization and container stats (JSONL)
            </p>
            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Button
                size="sm"
                appearance={view === 'table' ? 'primary' : 'subtle'}
                onClick={() => setView('table')}
              >
                Table
              </Button>
              {CHART_PARAMS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  appearance={view === p.key ? 'primary' : 'subtle'}
                  onClick={() => setView(p.key)}
                >
                  {p.label}
                </Button>
              ))}
              <Button
                size="sm"
                appearance={view === 'raw' ? 'primary' : 'subtle'}
                onClick={() => setView('raw')}
              >
                Raw JSONL
              </Button>
            </div>
            {chartParam ? (
              <MetricLineChart
                points={samples.map((s) => ({
                  label: tsLabel(s.ts),
                  value: numOrNull(chartParam.get(s)),
                }))}
                color={chartParam.color}
                unit={chartParam.unit}
                yMin={chartParam.yMin}
                yMax={chartParam.yMax}
              />
            ) : view === 'table' ? (
              <Table
                data={samples}
                autoHeight
                maxHeight={400}
                bordered
                cellBordered
                wordWrap="break-word"
              >
                <Column width={175} fixed>
                  <HeaderCell>Time (UTC)</HeaderCell>
                  <Cell>{(row) => tsFull(row.ts)}</Cell>
                </Column>
                <Column width={100}>
                  <HeaderCell>GPU Util %</HeaderCell>
                  <Cell>
                    {(row) => (
                      <span style={gpuUtilStyle(row.gpu)}>
                        {formatGpuUtil(row.gpu)}
                      </span>
                    )}
                  </Cell>
                </Column>
                <Column width={190}>
                  <HeaderCell>GPU Mem (used / total)</HeaderCell>
                  <Cell>{(row) => formatGpuMem(row.gpu)}</Cell>
                </Column>
                <Column width={90}>
                  <HeaderCell>Temp</HeaderCell>
                  <Cell>{(row) => formatGpuTemp(row.gpu)}</Cell>
                </Column>
                <Column width={90}>
                  <HeaderCell>Power</HeaderCell>
                  <Cell>{(row) => formatGpuPower(row.gpu)}</Cell>
                </Column>
                <Column width={90}>
                  <HeaderCell>CPU %</HeaderCell>
                  <Cell>{(row) => formatContainerCpu(row.container)}</Cell>
                </Column>
                <Column flexGrow={1} minWidth={160}>
                  <HeaderCell>Container Mem</HeaderCell>
                  <Cell>{(row) => formatContainerMem(row.container)}</Cell>
                </Column>
              </Table>
            ) : (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
              >
                {rawMetrics}
              </pre>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={fetchMetrics}
          appearance="subtle"
          icon={<Reload />}
          loading={loading}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button onClick={onClose} appearance="primary">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MetricsDialog;
